import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import {
  createPracticeEvent,
  searchPracticeEvents,
  getPracticeEvents,
  getPracticeEventById,
  IPracticeEvent,
  CreatePracticeEventDto,
  PracticeEventFilterDto
} from 'API/practice-event'

class ScheduleStore {
  rootStore: RootStore

  // List of events
  events: IPracticeEvent[] = []

  // Current event detail
  currentEvent: IPracticeEvent | null = null

  // UI states
  isManageModeInScheduleList: boolean = false
  isManageModeInAssignPage: boolean = false

  // Loading states
  isFetchingList: boolean = false
  isFetchingDetail: boolean = false
  isCreating: boolean = false

  // Pagination and count
  numberOfEvents: number = 0
  timeFetchingList: number = new Date().getTime()

  // Mock data for assigned user counts (temporary, will be replaced with BE integration)
  assignedUserCounts: Record<string, number> = {}

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  // Setters
  public setManageModeInScheduleList(status: boolean): void {
    this.isManageModeInScheduleList = status
  }

  public setManageModeInAssignPage(status: boolean): void {
    this.isManageModeInAssignPage = status
  }

  // Get assigned user count for lane/segment/station (mock data for now)
  public getAssignedUserCount(itemId: number, itemType: 'lane' | 'segment' | 'station'): number {
    const key = `${itemType}_${itemId}`
    // Return mock data or 0 if not found
    return this.assignedUserCounts[key] || 0
  }

  // API calls
  public async createEvent(data: CreatePracticeEventDto, orgId?: number): Promise<IPracticeEvent> {
    this.isCreating = true
    try {
      const event = await createPracticeEvent(data, orgId)
      this.events = [event, ...this.events]
      this.numberOfEvents = this.events.length
      return event
    } catch (error) {
      throw error
    } finally {
      this.isCreating = false
    }
  }

  public async searchEvents(
    filterDto: PracticeEventFilterDto,
    orgId?: number
  ): Promise<{ results: IPracticeEvent[]; totalCount: number }> {
    this.isFetchingList = true
    try {
      const { results, totalCount } = await searchPracticeEvents(filterDto, orgId)
      this.events = results
      this.numberOfEvents = totalCount
      this.timeFetchingList = new Date().getTime()
      return { results, totalCount }
    } catch (error) {
      return { results: [], totalCount: 0 }
    } finally {
      this.isFetchingList = false
    }
  }

  public async fetchEvents(
    params: {
      page?: number
      pageSize?: number
    },
    orgId?: number
  ): Promise<{ results: IPracticeEvent[]; totalCount: number }> {
    this.isFetchingList = true
    try {
      const { results, totalCount } = await getPracticeEvents(params, orgId)
      this.events = results
      this.numberOfEvents = totalCount
      this.timeFetchingList = new Date().getTime()
      return { results, totalCount }
    } catch (error) {
      return { results: [], totalCount: 0 }
    } finally {
      this.isFetchingList = false
    }
  }

  public async fetchEventDetail(eventId: number): Promise<IPracticeEvent | null> {
    this.isFetchingDetail = true
    try {
      const event = await getPracticeEventById(eventId)
      this.currentEvent = event
      return event
    } catch (error) {
      this.currentEvent = null
      return null
    } finally {
      this.isFetchingDetail = false
    }
  }

  public resetEventDetail(): void {
    this.currentEvent = null
  }

  /**
   * Drop every org/profile-scoped cache this store holds.
   *
   * Registered in `clearOrgScopedStores`, so switching play profile cannot leave
   * the previous profile's events on screen: the Schedule page only fetches when
   * it has an org to query, and a player with no organization has none — nothing
   * would have overwritten the coach's list.
   */
  public reset(): void {
    this.events = []
    this.currentEvent = null
    this.numberOfEvents = 0
    this.assignedUserCounts = {}
    this.isManageModeInScheduleList = false
    this.isManageModeInAssignPage = false
    this.isFetchingList = false
    this.isFetchingDetail = false
    this.isCreating = false
  }
}

export default ScheduleStore
